<template>
    <pendo-app
        class="vue-wrapper"
        :class="{
            'side-panel-open': panelOpen,
            'side-panel-resizing': sidePanelResizing,
            'vue-wrapper--framed': isAppFrameActive
        }"
        :style="{ '--side-panel-width': `${sidePanelWidth}px` }">
        <pendo-page
            ref="pendoPage"
            background-color="var(--surface-default-background)">
            <router-view-wrapper ref="routerViewWrapper">
                <ancient-app-version-banner
                    v-if="showAncientAppVersionBanner"
                    :days-out-of-date="daysOutOfDate" />
                <template v-if="!isOnTrialExpiredRoute">
                    <install-snippet-banner v-if="showInstallSnippetBanner" />
                    <sample-data-banner v-if="authenticated" />
                </template>
                <router-view :key="routerViewKey" />
            </router-view-wrapper>
        </pendo-page>
        <transition name="slide-panel">
            <side-panel-wrapper
                ref="sidePanel"
                :is-open="panelOpen"
                :render-content="panelContent"
                class="side-panel-wrapper">
                <template v-if="sidePanelCanResize">
                    <div
                        v-bind="sidePanelResizeHandleAttrs"
                        class="side-panel-wrapper__resize"
                        :class="{ 'is-dragging': sidePanelResizing }"
                        data-cy="side-panel-resize-handle"
                        :aria-label="$t('pendoDrawer.Resize_panel')"
                        aria-describedby="side-panel-resize-instructions">
                        <pendo-icon
                            class="side-panel-wrapper__resize-grip"
                            type="draggable-vertical"
                            size="12" />
                    </div>
                    <span
                        id="side-panel-resize-instructions"
                        class="side-panel-wrapper__resize-instructions">
                        {{ $t('pendoDrawer.Resize_instructions') }}
                    </span>
                </template>
            </side-panel-wrapper>
        </transition>
    </pendo-app>
</template>

<script>
import { mapState as mapPiniaState, mapActions as mapPiniaActions } from 'pinia';
import { useLeftNavStore } from '@/stores/left-nav.store';
import { canHaveGlobalBanner } from '@/global-banner/global-banner.utils';
import InstallSnippetBanner from '../free/install-snippet-banner/install-snippet-banner.vue';
import SampleDataBanner from '../free/sample-data-banner/sample-data-banner.vue';
import { PendoApp, PendoIcon, PendoPage, useResizablePanel } from '@pendo/components-vue3';
import SidePanelWrapper from './side-panel-wrapper/side-panel-wrapper.vue';
import { EventBus } from '@/utils/event-bus.js';
import { isOnReplayPlayerPage, isOnListenPage, isOnDataSyncPage, shouldShowNavItems } from '@/vue-router/route.utils';
import { useResizeObserver } from '@vueuse/core';
import { useTemplateRef, computed, nextTick, onMounted, ref, watch } from 'vue';
import { useFixedStyles } from '@/common/use/use-fixed-styles/use-fixed-styles';
import { tabbable } from 'tabbable';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import RouterViewWrapper from './router-view-wrapper.vue';
import AncientAppVersionBanner from './ancient-app-version-banner.vue';
import { useAuthStore } from '@/stores/auth.store';

const SIDE_PANEL_MIN_WIDTH = 377;
const SIDE_PANEL_MAX_WIDTH = 720;

export default {
    name: 'VueWrapper',
    components: {
        InstallSnippetBanner,
        SampleDataBanner,
        PendoApp,
        PendoIcon,
        PendoPage,
        RouterViewWrapper,
        SidePanelWrapper,
        AncientAppVersionBanner,
    },
    emits: ['unmount'],
    setup () {
        const pendoPage = useTemplateRef('pendoPage');
        const sidePanel = useTemplateRef('sidePanel');
        const panelOpen = ref(false);
        const {
            drawerStyles,
            affixStyles,
            setPendoPageRightMargin,
            setSidePanelRightInset,
        } = useFixedStyles();

        watch(
            () => drawerStyles.value,
            ({ top, right, bottom, pushDrawerShellChrome, pushDrawerShellBorderRadiusPx }) => {
                const root = document.querySelector(':root');
                root.style.setProperty('--drawer-top-position', `${top}px`);
                root.style.setProperty('--drawer-right-position', `${right}px`);
                root.style.setProperty('--drawer-bottom-position', `${bottom}px`);
                root.style.setProperty(
                    '--push-drawer-border-bottom-right-radius',
                    pushDrawerShellChrome ? `${pushDrawerShellBorderRadiusPx}px` : '0px',
                );
            },
            { immediate: true },
        );

        watch(
            () => affixStyles.value.fixedStyles.top,
            (top) => {
                const root = document.querySelector(':root');
                root.style.setProperty('--top-nav-height', `${top}px`);
            },
            { immediate: true },
        );

        const { width, canResize, isDragging, handleAttrs } = useResizablePanel({
            panel: () => (sidePanel.value?.$el instanceof Element ? sidePanel.value.$el : null),
            enabled: panelOpen,
            defaultWidth: SIDE_PANEL_MIN_WIDTH,
            minSize: SIDE_PANEL_MIN_WIDTH,
            maxSize: SIDE_PANEL_MAX_WIDTH,
            maxShare: 0.5,
            storageKey: 'side-panel',
            onResizeEnd: async (value) => {
                await nextTick();
                const pageTransitions = pendoPage.value?.$el?.getAnimations?.() ?? [];
                await Promise.allSettled(pageTransitions.map((transition) => transition.finished));
                EventBus.$emit('sidePanelResized', value);
            },
        });

        const sidePanelResizeHandleAttrs = computed(() => ({
            ...handleAttrs.value,
            onKeydown: (event) => {
                if (event.key === 'Escape') {
                    panelOpen.value = false;

                    return;
                }

                handleAttrs.value.onKeydown(event);
            },
        }));

        watch([panelOpen, width], ([isOpen, panelWidth]) => setSidePanelRightInset(isOpen ? panelWidth : 0));

        onMounted(() => {
            useResizeObserver(pendoPage, (entries) => {
                const entry = entries[0];
                const { width: pendoPageWidth } = entry.contentRect;
                setPendoPageRightMargin(pendoPageWidth);
            });
        });

        return {
            pendoPage,
            panelOpen,
            sidePanelWidth: width,
            sidePanelCanResize: canResize,
            sidePanelResizing: isDragging,
            sidePanelResizeHandleAttrs,
            setSidePanelRightInset,
        };
    },
    data () {
        return {
            appAgeInterval: null,
            daysOutOfDate: 0,
            panelContent: () => null,
        };
    },
    computed: {
        ...mapPiniaState(useAuthStore, {
            authenticated: (store) => store.authenticated,
        }),
        ...mapPiniaState(useLeftNavStore, ['isTopBannerDismissed']),
        isOnTrialExpiredRoute () {
            return this.$route?.name === 'TrialExpired';
        },
        canHaveGlobalBanner () {
            const params = {
                isTopBannerDismissed: this.isTopBannerDismissed,
                currentRoute: this.$route?.name,
            };

            return canHaveGlobalBanner(params);
        },
        routerViewKey () {
            // This is a workaround to prevent the router-view from remounting when navigating
            // between different pages in the Listen module
            if (isOnListenPage(this.$route)) {
                return 'pendo-listen';
            }
            // This is a workaround to prevent the router-view from remounting when navigating
            // between replays in the queue
            if (isOnReplayPlayerPage(this.$route.path)) {
                return 'replay-player';
            }
            // This is a workaround to prevent the router-view from remounting when updating
            // the URL based on progress through the data-sync setup flow
            if (isOnDataSyncPage(this.$route.path)) {
                return 'data-sync';
            }

            return this.$route?.path || 'pendo-app';
        },
        showInstallSnippetBanner () {
            return !this.$productLine(PRODUCT_LINES.ADOPT);
        },
        isAppFrameActive () {
            return shouldShowNavItems(this.$route, this.authenticated);
        },
        showAncientAppVersionBanner () {
            return this.daysOutOfDate > 6;
        },
    },
    watch: {
        '$route.path': {
            handler (newPath, oldPath) {
                // Close any open SidePanel on navigation — the panel is scoped
                // to the page that opened it (activity blade, event-property
                // blade, etc.), so leaving it up after route change is
                // disorienting. `oldPath` is undefined on the immediate call.
                if (oldPath && newPath !== oldPath && this.panelOpen) {
                    this.onCloseSidePanel();
                }
                setTimeout(() => {
                    const routerViewWrapper = this.$refs.routerViewWrapper?.$el || this.$refs.routerViewWrapper;

                    if (!routerViewWrapper) {
                        return;
                    }

                    // focus the first heading if it exists
                    const firstHeading = routerViewWrapper.querySelector?.('h1');

                    if (firstHeading) {
                        firstHeading.setAttribute('tabindex', '-1');
                        firstHeading.style.outline = 'none';
                        firstHeading.focus();

                        return;
                    }

                    // focus the first tabbable element if it exists
                    const [firstTabbableElement] = tabbable(routerViewWrapper);

                    if (firstTabbableElement) {
                        firstTabbableElement.focus();

                        return;
                    }

                    // focus the router-view-wrapper if no other element is focusable
                    routerViewWrapper.setAttribute('tabindex', '-1');
                    routerViewWrapper.focus();
                }, 200);
            },
            flush: 'post',
            immediate: true,
        },
    },
    created () {
        EventBus.$on('openSidePanel', this.onOpenSidePanel);
        EventBus.$on('closeSidePanel', this.onCloseSidePanel);
        EventBus.$on('sidePanelClosed', this.onSidePanelClosed);
        EventBus.$on('closeTopBanner', this.onCloseTopBanner);
    },
    mounted () {
        if (!this.canHaveGlobalBanner) {
            this.setTopBannerHeight({ topBannerHeight: 0 });
        }

        const fourHours = 60 * 1000 * 60 * 4;
        const oneDay = 60 * 1000 * 60 * 24;

        this.appAgeInterval = window.setInterval(() => {
            const timeDiff = Date.now() - window.__appLoadDate.getTime();
            this.daysOutOfDate = parseInt(timeDiff / oneDay);
        }, fourHours);
    },
    beforeUnmount () {
        window.clearInterval(this.appAgeInterval);
        this.setSidePanelRightInset(0);
        EventBus.$off('openSidePanel', this.onOpenSidePanel);
        EventBus.$off('closeSidePanel', this.onCloseSidePanel);
        EventBus.$off('sidePanelClosed', this.onSidePanelClosed);
        EventBus.$off('closeTopBanner', this.onCloseTopBanner);
    },
    methods: {
        ...mapPiniaActions(useLeftNavStore, ['setTopBannerDismissed', 'setTopBannerHeight']),
        onOpenSidePanel (renderPanel) {
            const wasAlreadyOpen = this.panelOpen;
            this.panelOpen = true;
            this.panelContent = renderPanel;
            this.$nextTick(() => {
                setTimeout(() => {
                    EventBus.$emit('focusSidePanel');
                }, wasAlreadyOpen ? 50 : 400);
            });
        },
        onCloseSidePanel () {
            this.panelOpen = false;
        },
        onSidePanelClosed () {
            this.panelOpen = false;
        },
        onCloseTopBanner () {
            this.setTopBannerDismissed({ isTopBannerDismissed: true });
        },
    },
};
</script>

<style lang="less" scoped>
.vue-wrapper {
    display: flex !important;
    flex-direction: column;
    flex-grow: 1;
    position: relative !important;
    inset: unset !important;

    .slide-panel-enter-active,
    .slide-panel-leave-active {
        transition: transform 0.3s;
    }

    .slide-panel-enter-to,
    .slide-panel-leave-from {
        transform: translateX(0);
    }

    .slide-panel-enter-from,
    .slide-panel-leave-to {
        transform: translateX(100%);
    }

    .pendo-page {
        transition: margin-right 220ms;
        position: relative !important;
    }

    .side-panel-wrapper {
        z-index: 1;
        background-color: var(--overlay-modal-background-base);
        width: var(--side-panel-width);
        position: fixed;
        right: 0;
        top: var(--drawer-top-position);
        bottom: 0;
    }

    .side-panel-wrapper__resize {
        position: absolute;
        top: 0;
        bottom: 0;
        left: -6px;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 13px;
        cursor: col-resize;
        touch-action: none;
        outline: none;
        color: var(--drawer-resize-handle-icon-base);

        &::before {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            left: 6px;
            width: 1px;
            background-color: var(--drawer-resize-divider-base);
            transform-origin: right center;
            transition:
                background-color 0.2s ease-in-out,
                transform 0.2s ease-in-out;
        }

        &:hover,
        &:focus-visible,
        &.is-dragging {
            color: var(--drawer-resize-handle-icon-hover);

            &::before {
                background-color: var(--drawer-resize-divider-hover);
                transform: scaleX(2);
                transition: background-color 0.2s ease-in-out;
            }

            .side-panel-wrapper__resize-grip {
                opacity: 1;
                background-color: var(--drawer-resize-handle-background-hover);
            }
        }

        &:focus-visible .side-panel-wrapper__resize-grip {
            outline: 2px solid var(--drawer-resize-focus-ring-base);
        }
    }

    .side-panel-wrapper__resize-grip {
        position: relative;
        padding: 4px 2px;
        border-radius: 2px;
        background-color: var(--drawer-resize-handle-background-base);
        box-shadow: 0 1px 1px 0 var(--drawer-resize-handle-drop-shadow);
        transition:
            background-color 0.1s ease-in-out,
            opacity 0.2s ease-in-out;

        @media (hover: hover) {
            opacity: 0;
        }
    }

    .side-panel-wrapper__resize-instructions {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
    }

    &.side-panel-open {
        .pendo-page {
            margin-right: var(--side-panel-width);
        }
    }

    &.side-panel-resizing .pendo-page {
        transition: none;
    }

    &--framed {
        overflow: hidden;

        .side-panel-wrapper {
            position: absolute;
            top: 0;
        }
    }
}
</style>
